import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Add, ClearRounded, Close } from "@material-ui/icons";
import { DropzoneArea } from "material-ui-dropzone";
import moment from "moment";
import { useMutation, useNotify } from "ra-core";
import React from "react";
import { SelectInput, SimpleForm, TextInput } from "react-admin";
import { Booking } from "../../types";
import { OrderActions } from "./util";

interface Props {
  data: Booking;
}

interface InvoiceData {
  id: number;
  total_amount: string;
  description: string;
}

interface TimeCase {
  id: number;
  timeCaseId: string;
  invoiceDatas: InvoiceData[];
  reportNote: string;
  files: File[];
  invoiceNumber: string;
  invoiceAmount: string;
}

const SubmitReportDialog: React.FC<Props> = (props) => {
  const { data } = props;

  const [open, setOpen] = React.useState(false);

  // Multiple time cases state
  const [timeCases, setTimeCases] = React.useState<TimeCase[]>([
    {
      id: 1,
      timeCaseId: "",
      invoiceDatas: [],
      reportNote: "",
      files: [],
      invoiceNumber: "",
      invoiceAmount: "",
    },
  ]);
  const [nextTimeCaseId, setNextTimeCaseId] = React.useState(2);
  const [nextInvoiceId, setNextInvoiceId] = React.useState(1);

  const notify = useNotify();
  const [mutate] = useMutation();

  // Get available surgery dates from booking (excluding dates that already have reports)
  const surgeryDateOptions = React.useMemo(() => {
    // Get list of booking_date_ids that already have reports
    const reportedDateIds =
      data.reports && Array.isArray(data.reports)
        ? data.reports
            .map((report: any) => String(report.booking_date_id))
            .filter(Boolean)
        : [];

    if (data.dates && Array.isArray(data.dates) && data.dates.length > 0) {
      return data.dates
        .map((dateObj: any, index: number) => {
          // Handle both object format {id, date} and string format
          const dateId =
            typeof dateObj === "object" && dateObj?.id ? dateObj.id : dateObj;
          const dateValue =
            typeof dateObj === "string" ? dateObj : dateObj?.date;
          const formattedDate = moment(dateValue).format(
            "DD MMM YYYY, hh:mm A",
          );
          return {
            id: String(dateId), // Use the actual ID from the date object
            name: `Case ${index + 1}: ${formattedDate}`,
          };
        })
        .filter((option) => !reportedDateIds.includes(option.id)); // Exclude dates with existing reports
    } else if (data.date) {
      // Fallback to old single date format
      const formattedDate = moment(data.date).format("DD MMM YYYY, hh:mm A");
      // Check if this single date already has a report
      const hasReport = reportedDateIds.includes(String(data.date));
      return hasReport
        ? []
        : [
            {
              id: data.date,
              name: formattedDate,
            },
          ];
    }
    return [];
  }, [data]);

  // Get available options for a specific time case (excluding already selected dates)
  const getAvailableOptionsForTimeCase = React.useCallback(
    (currentTimeCaseId: number) => {
      const selectedDates = timeCases
        .filter((tc) => tc.id !== currentTimeCaseId && tc.timeCaseId)
        .map((tc) => tc.timeCaseId);

      return surgeryDateOptions.filter(
        (option) => !selectedDates.includes(option.id),
      );
    },
    [timeCases, surgeryDateOptions],
  );

  // Check if we can add more time cases (all dates must not be assigned yet)
  const canAddMoreTimeCases = React.useMemo(() => {
    return timeCases.length < surgeryDateOptions.length;
  }, [timeCases.length, surgeryDateOptions.length]);

  // Time case handlers
  const addTimeCase = () => {
    setTimeCases([
      ...timeCases,
      {
        id: nextTimeCaseId,
        timeCaseId: "",
        invoiceDatas: [],
        reportNote: "",
        files: [],
        invoiceNumber: "",
        invoiceAmount: "",
      },
    ]);
    setNextTimeCaseId((prev) => prev + 1);
  };

  const removeTimeCase = (timeCaseId: number) => {
    setTimeCases(timeCases.filter((tc) => tc.id !== timeCaseId));
  };

  const updateTimeCase = (
    timeCaseId: number,
    field: keyof TimeCase,
    value: any,
  ) => {
    setTimeCases(
      timeCases.map((tc) =>
        tc.id === timeCaseId ? { ...tc, [field]: value } : tc,
      ),
    );
  };

  // Invoice handlers
  const addInvoiceData = (timeCaseId: number) => {
    setTimeCases(
      timeCases.map((tc) =>
        tc.id === timeCaseId
          ? {
              ...tc,
              invoiceDatas: [
                ...tc.invoiceDatas,
                { id: nextInvoiceId, total_amount: "", description: "" },
              ],
            }
          : tc,
      ),
    );
    setNextInvoiceId((prev) => prev + 1);
  };

  const removeInvoiceData = (timeCaseId: number, invoiceId: number) => {
    setTimeCases(
      timeCases.map((tc) =>
        tc.id === timeCaseId
          ? {
              ...tc,
              invoiceDatas: tc.invoiceDatas.filter(
                (inv) => inv.id !== invoiceId,
              ),
            }
          : tc,
      ),
    );
  };

  const updateInvoiceData = (
    timeCaseId: number,
    invoiceId: number,
    field: "total_amount" | "description",
    value: string,
  ) => {
    setTimeCases(
      timeCases.map((tc) =>
        tc.id === timeCaseId
          ? {
              ...tc,
              invoiceDatas: tc.invoiceDatas.map((inv) =>
                inv.id === invoiceId ? { ...inv, [field]: value } : inv,
              ),
            }
          : tc,
      ),
    );
  };

  const handleAccept = () => {
    // Validate that all time cases have a selected surgery date
    const invalidTimeCases = timeCases.filter((tc) => !tc.timeCaseId);
    if (invalidTimeCases.length > 0) {
      notify("Please select a surgery date/time for all time cases", "warning");
      return;
    }

    // Check for duplicate surgery dates
    const selectedDates = timeCases.map((tc) => tc.timeCaseId);
    const uniqueDates = new Set(selectedDates);
    if (selectedDates.length !== uniqueDates.size) {
      notify("Each time case must have a unique surgery date/time", "warning");
      return;
    }

    // 1. Initialize FormData
    const formData = new FormData();

    // 2. Append simple fields
    formData.append("action", OrderActions.SUBMIT_REPORT);

    // 3. Append Reports with nested structure
    timeCases.forEach((timeCase, reportIndex) => {
      // Append booking_date_id (the selected surgery date ID)
      formData.append(
        `reports[${reportIndex}][booking_date_id]`,
        timeCase.timeCaseId,
      );

      // Append invoice_number and invoice_amount at report level
      if (timeCase.invoiceNumber) {
        formData.append(
          `reports[${reportIndex}][invoice_number]`,
          timeCase.invoiceNumber,
        );
      }
      if (timeCase.invoiceAmount) {
        formData.append(
          `reports[${reportIndex}][invoice_amount]`,
          timeCase.invoiceAmount,
        );
      }

      // Append report_note for this time case
      if (timeCase.reportNote) {
        formData.append(
          `reports[${reportIndex}][report_note]`,
          timeCase.reportNote,
        );
      }

      // Append invoices for this report
      const validInvoices = timeCase.invoiceDatas.filter(
        (inv) =>
          inv.total_amount.trim() !== "" && inv.description.trim() !== "",
      );

      validInvoices.forEach((invoice, invIndex) => {
        formData.append(
          `reports[${reportIndex}][invoices][${invIndex}][total_amount]`,
          invoice.total_amount,
        );
        formData.append(
          `reports[${reportIndex}][invoices][${invIndex}][description]`,
          invoice.description,
        );
      });

      // Append files/medias for this report (after invoices)
      timeCase.files.forEach((file, fileIndex) => {
        formData.append(
          `reports[${reportIndex}][medias][${fileIndex}][url]`,
          file,
        );
        formData.append(
          `reports[${reportIndex}][medias][${fileIndex}][type]`,
          "image",
        );
      });
    });

    // 4. Execute Mutation
    mutate(
      {
        type: "update",
        resource: "bookings",
        payload: {
          id: data.id ?? "",
          data: formData,
          query: {
            _method: "PUT",
          },
        },
      },
      {
        onSuccess: () => {
          notify(`Successfully submitted the report.`, "info");
          handleClose();
        },
        onFailure: (error: any) => notify(`Error: ${error.message}`, "warning"),
      },
    );
  };

  const handleClickOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    // Reset state
    setTimeCases([
      {
        id: 1,
        timeCaseId: "",
        invoiceDatas: [],
        reportNote: "",
        files: [],
        invoiceNumber: "",
        invoiceAmount: "",
      },
    ]);
    setNextTimeCaseId(2);
    setNextInvoiceId(1);
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleClickOpen}
        size={"small"}
        variant="contained"
      >
        Submit Report
      </Button>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleClose}
        disableBackdropClick={true}
      >
        <DialogTitle id="alert-dialog-title">
          Submit Report
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Submit report data for each surgery date/time case.
          </DialogContentText>

          {/* Booking Information Summary */}
          <Card style={{ marginBottom: 16, backgroundColor: "#f5f5f5" }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Booking Information</strong>
              </Typography>
              <Typography variant="body2">
                <strong>Hospital:</strong> {data.hospital?.name || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Surgeon:</strong> {data.surgeon?.full_name || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Procedure:</strong> {data.procedure?.name || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Reference:</strong> BK-{data.id}
              </Typography>
            </CardContent>
          </Card>

          {/* Time Cases */}
          {timeCases.map((timeCase, tcIndex) => (
            <Card
              key={timeCase.id}
              style={{
                marginBottom: 16,
                border: "1px solid #e0e0e0",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  marginBottom={2}
                >
                  <Typography variant="h6">Time Case #{tcIndex + 1}</Typography>
                  {timeCases.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeTimeCase(timeCase.id)}
                      style={{ color: "red" }}
                    >
                      <Close />
                    </IconButton>
                  )}
                </Box>

                <SimpleForm toolbar={<></>}>
                  {/* Surgery Date/Time Selector */}
                  <SelectInput
                    source={`time_case_${timeCase.id}_date`}
                    label="Select Surgery Date/Time *"
                    choices={getAvailableOptionsForTimeCase(timeCase.id)}
                    value={timeCase.timeCaseId}
                    onChange={(event) =>
                      updateTimeCase(
                        timeCase.id,
                        "timeCaseId",
                        event.target.value,
                      )
                    }
                    fullWidth
                    helperText={
                      getAvailableOptionsForTimeCase(timeCase.id).length === 0
                        ? "All surgery dates have been assigned to other time cases"
                        : "Select a surgery date/time for this case"
                    }
                  />

                  {/* Invoice Data Section */}
                  <Box marginTop={2} marginBottom={2}>
                    <Typography
                      variant="subtitle2"
                      style={{ marginBottom: 8, fontWeight: 600 }}
                    >
                      Invoice Items
                    </Typography>
                    {timeCase.invoiceDatas.map((invoiceData, invIndex) => (
                      <Box
                        key={invoiceData.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <TextInput
                          source={`tc_${timeCase.id}_total_amount_${invoiceData.id}`}
                          label={`Total Amount ${invIndex + 1}`}
                          value={invoiceData.total_amount}
                          type="number"
                          onChange={(event) =>
                            updateInvoiceData(
                              timeCase.id,
                              invoiceData.id,
                              "total_amount",
                              event.target.value,
                            )
                          }
                        />
                        <TextInput
                          source={`tc_${timeCase.id}_description_${invoiceData.id}`}
                          label={`Description ${invIndex + 1}`}
                          value={invoiceData.description}
                          onChange={(event) =>
                            updateInvoiceData(
                              timeCase.id,
                              invoiceData.id,
                              "description",
                              event.target.value,
                            )
                          }
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            removeInvoiceData(timeCase.id, invoiceData.id)
                          }
                          style={{ color: "red" }}
                        >
                          <ClearRounded />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<Add />}
                      style={{
                        backgroundColor: "darkblue",
                        color: "white",
                        marginTop: 8,
                      }}
                      size="small"
                      onClick={() => addInvoiceData(timeCase.id)}
                    >
                      Add Invoice Item
                    </Button>
                  </Box>

                  {/* Report Note */}
                  <TextInput
                    source={`tc_${timeCase.id}_report_note`}
                    label="Report Note"
                    value={timeCase.reportNote}
                    multiline
                    fullWidth
                    onChange={(event) =>
                      updateTimeCase(
                        timeCase.id,
                        "reportNote",
                        event.target.value,
                      )
                    }
                  />
                </SimpleForm>

                {/* Upload Files */}
                <Box marginTop={2}>
                  <Typography
                    variant="subtitle2"
                    style={{ marginBottom: 8, fontWeight: 600 }}
                  >
                    Upload Files
                  </Typography>
                  <DropzoneArea
                    filesLimit={10}
                    maxFileSize={30 * 1024 * 1024}
                    onChange={(files) =>
                      updateTimeCase(timeCase.id, "files", files)
                    }
                    initialFiles={timeCase.files}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}

          {/* Add More Time Case Button */}
          <Button
            startIcon={<Add />}
            variant="outlined"
            color="primary"
            fullWidth
            onClick={addTimeCase}
            disabled={!canAddMoreTimeCases}
            style={{ marginTop: 8 }}
          >
            {canAddMoreTimeCases
              ? `Add More Time Case (${timeCases.length}/${surgeryDateOptions.length})`
              : `All Surgery Dates Assigned (${timeCases.length}/${surgeryDateOptions.length})`}
          </Button>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAccept} color="primary" autoFocus>
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubmitReportDialog;
